import React, { useState, useEffect, useRef } from "react";
import { createProject } from "../lib/projects";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import PortalNavbar from "./PortalNavbar";
import ProjectsPage from "./ProjectsPage";
import ProjectDetailPage from "./ProjectDetailPage";
import NavigationBar from "./NavigationBar";
import ContentGenerator from "./ContentGeneration/ContentGenerator";
import supabase from "../supabase";
import Members from "./ManageUsers/Members";
import Messages from "./Messages";
import FileManager from "./FileManager";
import FileManagerPage from "./FileManagerPage";
import CreateProjectModal from "./CreateProjectModal";
import Dashboard from "./Dashboard";
import EditProfile from "./EditProfile";
import RequestsPage from "./RequestsPage";
import RequestsDetailsPage from "./RequestsDetailsPage";
import RequestCenterPage from "./RequestCenterPage";

function MyContentPage({ profile, setFeedback, organization }) {
  const navigate = useNavigate();
  const { companyId } = useParams();

  const beginGenerating = () => {};
  const endGenerating = () => {};

  return (
    <FileManagerPage
      beginGenerating={beginGenerating}
      endGenerating={endGenerating}
      profile={profile}
      setFeedback={setFeedback}
      orgId={profile?.company_id}
      currentUserId={profile?.id}
      currentUserRole={profile?.role}
      organization={organization}
      onOpenArticle={(article) => {
        // Send the chosen article to the editor route
        navigate(`/organization-portal/${companyId}/content-generation`, {
          state: { openArticle: article },
        });
      }}
    />
  );
}

const OrganizationPortal = () => {
  const location = useLocation();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [errorProfile, setErrorProfile] = useState("");
  const [organization, setOrganization] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [errorOrg, setErrorOrg] = useState("");
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [feedback, setFeedback] = useState({
    overall: 0,
    readability: 0,
    topicAuthority: 0,
    contentValue: 0,
    seo: 0,
    metaTags: "",
    improvements: {},
  });

  // Tracks whether the article editor currently has a never-saved draft with
  // real content, and how to save it, so sidebar/profile-menu navigation can
  // prompt before leaving it behind.
  const unsavedDraftRef = useRef({ hasUnsaved: false, saveAs: null });
  const handleUnsavedDraftChange = (state) => {
    unsavedDraftRef.current = state;
  };
  const confirmAndMaybeSave = async () => {
    if (!unsavedDraftRef.current.hasUnsaved) return;
    const shouldSave = window.confirm(
      "You have an unsaved article. Save it before leaving?"
    );
    if (shouldSave && unsavedDraftRef.current.saveAs) {
      await unsavedDraftRef.current.saveAs();
    }
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
    //console.log("COLLAPSED:", collapsed);
  };

  // Function to trigger Highcharts reflow
  const triggerChartResize = () => {
    window.dispatchEvent(new Event("resize"));
  };

  const handleCreateProject = async (formData) => {
    try {
      await createProject({
        title: formData.title,
        organization: formData.org_name, // map to schema
        company_id: profile?.company_id, // required
        created_by: profile?.id, // required
        writers: formData.writer_ids ?? null, // nullable array
        editors: formData.editor_ids ?? null, // nullable array
      });
      alert("Project created!");
    } catch (err) {
      console.error("Error creating project:", err.message);
      alert("Failed to create project.");
    }
  };

  useEffect(() => {
    triggerChartResize();
  }, [collapsed]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);

        // Step 1: Get the authenticated user
        const { data: user, error: authError } = await supabase.auth.getUser();

        if (authError || !user?.user) {
          navigate("/login", {
            state: { from: location.pathname + location.search },
          });
          return;
        }

        // Step 2: Query the Users table for the user's profile
        const { data: profileData, error: profileError } = await supabase
          .from("Users")
          .select("*")
          .eq("id", user.user.id)
          .single();

        if (profileError)
          throw new Error(`Failed to fetch profile: ${profileError.message}`);

        // 🚨 THE SUPER ADMIN INTERCEPTOR 🚨
        // Check local storage for an active impersonation token
        const overrideOrgId = localStorage.getItem("superAdminOrgOverride");
        if (profileData.role === "superAdmin" && overrideOrgId) {
          profileData.company_id = overrideOrgId;
          profileData.role = "Admin";
          profileData.isTrueSuperAdmin = true;
        }

        setProfile(profileData);
      } catch (error) {
        setErrorProfile(error.message);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        setLoadingOrg(true);

        // Query the Organizations table for the company_id
        const { data, error } = await supabase
          .from("Organizations") // Replace with your table name
          .select("*")
          .eq("id", companyId)
          .single();

        if (error) {
          throw error;
        }

        //console.log("Organization Data:", data);
        setOrganization(data);
      } catch (err) {
        console.error("Error fetching organization:", err.message);
        setErrorOrg(err.message);
      } finally {
        setLoadingOrg(false);
      }
    };

    if (companyId) {
      fetchOrganization();
    }
  }, [companyId]);

  useEffect(() => {
    function handleOpenModal() {
      setProjectModalOpen(true);
    }
    window.addEventListener("openProjectModal", handleOpenModal);
    return () =>
      window.removeEventListener("openProjectModal", handleOpenModal);
  }, []);

  if (loadingProfile) return <div>Loading...</div>;
  if (errorProfile) return <div>Error: {errorProfile}</div>;
  if (loadingOrg) return <div>Loading...</div>;
  if (errorOrg) return <div>Error: {errorOrg}</div>;

  //console.log("Profile Data: ", profile);
  return (
    <div style={{ display: "flex", width: "100%", minHeight: "100%" }}>
      <PortalNavbar
        collapsed={collapsed}
        toggleCollapsed={toggleCollapsed}
        profile={profile}
        organization={organization}
        onGuardNavigate={confirmAndMaybeSave}
      />
      <div
        style={{
          flex: 1,
          width: collapsed ? "92%" : "80%",
          transition: "width 0.3s ease",
          //backgroundColor: "#eeee",
        }}
      >
        {/* ✅ NavigationBar now uses profile prop */}
        <NavigationBar
          profile={profile}
          key={profile?.id || "loading"}
          onGuardNavigate={confirmAndMaybeSave}
        />

        <div style={{ padding: "20px 30px" }}>
          <Routes>
            <Route
              path=""
              element={<Dashboard profile={profile} organization={organization} />}
            />

            {/* ✅ NEW: Edit Profile Route */}
            <Route
              path="edit-profile"
              element={<EditProfile profile={profile} />}
            />

            <Route
              path="content-generation"
              element={
                <ContentGenerator
                  profile={profile}
                  currentUserRole={profile.role}
                  feedback={feedback}
                  setFeedback={setFeedback}
                  organization={organization}
                  onUnsavedDraftChange={handleUnsavedDraftChange}
                />
              }
            />
            <Route
              path="my-content"
              element={
                <MyContentPage
                  profile={profile}
                  setFeedback={setFeedback}
                  organization={organization}
                />
              }
            />
            <Route
              path="messages"
              element={<Messages profile={profile} organization={organization} />}
            />
            <Route
              path="members"
              element={
                <Members profile={profile} organization={organization} />
              }
            />
            <Route
              path="projects"
              element={
                <ProjectsPage profile={profile} organization={organization} />
              }
            />
            <Route
              path="projects/:projectId"
              element={
                <ProjectDetailPage
                  profile={profile}
                  setFeedback={setFeedback}
                  organization={organization}
                />
              }
            />
            <Route
              path="requests"
              element={
                <RequestsPage profile={profile} organization={organization} />
              }
            />
            <Route
              path="requests/:requestId"
              element={
                <RequestsDetailsPage
                  organization={organization}
                  profile={profile}
                />
              }
            />
            <Route
              path="request-center"
              element={
                <RequestCenterPage profile={profile} organization={organization} />
              }
            />
          </Routes>
        </div>
      </div>
      {projectModalOpen && (
        <CreateProjectModal
          isOpen={projectModalOpen}
          onClose={() => setProjectModalOpen(false)}
          onCreate={handleCreateProject}
          orgId={profile?.company_id}
          currentUserRole={profile?.role} // ← make sure this is passed
        />
      )}
    </div>
  );
};

export default OrganizationPortal;
